import React, { Fragment, useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import PropTypes from "prop-types";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import Skeleton from "../../components/ui-components/Skeleton";
import KpiCard from "../../components/ui-components/KpiCard";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import FilterDropdown from "../../components/ui-components/FilterDropdown";
import { formatMoney, formatRelativeTime } from "../Budgets/formatters";
import {
  useApprovalsInboxQuery,
  useApproveApprovalRequestMutation,
  useRejectApprovalRequestMutation,
  useReturnApprovalRequestMutation,
} from "./hooks/useApprovalsQueries";
import { toast } from "../../components/ui-components/Toast";
import { useCurrentUser } from "../../api/auth";

const REJECTION_CATEGORIES = [
  { value: "insufficient_budget", label: "Insufficient Budget" },
  { value: "policy_violation", label: "Policy Violation" },
  { value: "duplicate_request", label: "Duplicate Request" },
  { value: "incomplete_info", label: "Incomplete Information" },
  { value: "other", label: "Other" },
];

// TODO: Backend does not yet return risk_profile, budget_delta, department, or requester_name.
// These fields are derived or stubbed until the API is extended.

/** Decision line 1: "Budget increase" | "Budget decrease" from impact sign or transition_type. */
function getDecisionLabel(impactAmount, transitionType) {
  if (typeof impactAmount === "number") {
    return impactAmount >= 0 ? "Budget increase" : "Budget decrease";
  }
  const t = (transitionType || "").toLowerCase();
  if (t === "increase" || t === "activate") return "Budget increase";
  if (t === "decrease") return "Budget decrease";
  return "Budget change";
}

/** Decision line 2: "Budget name - Period" when available; else fallback. */
function getDecisionContext(item) {
  const name = item.budget_name;
  const period = item.period_label;
  if (name && period) return `${name} - ${period}`;
  if (item.object_type && item.object_id) {
    const typeLabel = (item.object_type || "").replaceAll("_", " ");
    return `${typeLabel} #${item.object_id}`;
  }
  return "—";
}

/** Impact line 1: "+ $X" or "- $X" (requested change amount). */
function formatImpactAmount(impactAmount, currency = "USD") {
  if (impactAmount == null || !Number.isFinite(impactAmount)) return "—";
  const cents = Math.round(Number(impactAmount) * 100);
  const formatted = formatMoney(Math.abs(cents), currency);
  return cents >= 0 ? `+ ${formatted}` : `- ${formatted}`;
}

/** Impact line 2: "+ X%" or "- X%" when previous_amount available; else "—". */
function formatImpactPercent(impactAmount, previousAmount) {
  if (impactAmount == null || previousAmount == null || previousAmount === 0 || !Number.isFinite(Number(previousAmount))) return "—";
  const amt = Number(impactAmount);
  const prev = Number(previousAmount);
  const pct = prev === 0 ? 0 : (amt / prev) * 100;
  const sign = pct >= 0 ? "+" : "-";
  return `${sign} ${Math.abs(pct).toFixed(1)}%`;
}

// Shared comment-required popover for Reject and Return — both are hard-SoD-gated
// decisions that require the same category + free-text prompt; only the copy differs.
function RejectionPopover({ anchorRef, onClose, onConfirm, isSubmitting, action = "reject" }) {
  const isReturn = action === "return";
  const [category, setCategory] = useState(REJECTION_CATEGORIES[0].value);
  const [comment, setComment] = useState("");
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorRef]);

  return (
    <div
      ref={popoverRef}
      className="absolute right-0 top-full mt-2 z-50 w-[280px] bg-card border border-card-bd rounded-card p-4"
      onClick={(e) => e.stopPropagation()}
      // Without this, typing a space (or pressing Enter) in the comment field bubbles a
      // keydown up to the table row, which treats Space/Enter as "activate row" and
      // navigates away mid-edit — pre-existing bug, surfaced by typing a multi-word
      // comment into either this popover (Reject) or its Return counterpart.
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-text">
          {isReturn ? "Return Reason" : "Rejection Reason"}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-text-2 hover:text-text cursor-pointer"
        >
          <FontAwesomeIcon icon={icon({ name: "xmark", family: "sharp", style: "solid" })} className="text-sm" />
        </button>
      </div>

      {!isReturn && (
        <>
          <label htmlFor="reject-category" className="block text-xs font-medium text-text-2 mb-1">Category</label>
          <select
            id="reject-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full border border-border rounded-ctrl px-2 py-1.5 text-sm mb-3 bg-card focus:outline-none focus:border-border-strong"
          >
            {REJECTION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </>
      )}

      <label htmlFor="reject-comments" className="block text-xs font-medium text-text-2 mb-1">
        {isReturn ? "What needs to change (required)" : "Comments"}
      </label>
      <textarea
        id="reject-comments"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={
          isReturn
            ? "Explain what needs to change before this can be resubmitted..."
            : "Provide context for the requestor..."
        }
        className="w-full border border-border rounded-ctrl px-2 py-1.5 text-sm min-h-[70px] resize-none focus:outline-none focus:border-border-strong"
      />

      <div className="flex justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="px-3 py-1.5 text-sm text-text hover:bg-surface-2 rounded-ctrl cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm({ category, comment })}
          disabled={isSubmitting || (isReturn && !comment.trim())}
          className={`px-3 py-1.5 text-sm text-white rounded-ctrl cursor-pointer disabled:opacity-50 hover:opacity-90 ${
            isReturn ? "bg-warning" : "bg-danger"
          }`}
        >
          {isSubmitting
            ? isReturn
              ? "Returning..."
              : "Rejecting..."
            : isReturn
              ? "Confirm Return"
              : "Confirm Reject"}
        </button>
      </div>
    </div>
  );
}

RejectionPopover.propTypes = {
  anchorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  action: PropTypes.oneOf(["reject", "return"]),
};

const ApprovalsInboxPage = () => {
  const navigate = useNavigate();
  const { data: inbox, isLoading, error, refetch } = useApprovalsInboxQuery();
  const { data: currentUser } = useCurrentUser();
  const approveMutation = useApproveApprovalRequestMutation();
  const rejectMutation = useRejectApprovalRequestMutation();
  const returnMutation = useReturnApprovalRequestMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  // { id, action } for the open comment popover, or null when closed. action is
  // "reject" | "return" — both share the same required-comment prompt (RejectionPopover).
  const [decisionPopover, setDecisionPopover] = useState(null);
  const decisionBtnRef = useRef(null);

  const filteredInbox = useMemo(() => {
    if (!inbox) return [];
    if (!searchQuery.trim()) return inbox;
    const q = searchQuery.toLowerCase();
    return inbox.filter(
      (item) =>
        (item.object_type || "").toLowerCase().includes(q) ||
        (item.object_id || "").toLowerCase().includes(q) ||
        (item.requested_by || "").toLowerCase().includes(q) ||
        (item.impact_summary || "").toLowerCase().includes(q)
    );
  }, [inbox, searchQuery]);

  const totalCount = filteredInbox.length;

  const totalValue = useMemo(() => {
    if (!inbox) return 0;
    return inbox.reduce((sum, item) => sum + (Number(item.impact_amount) || 0), 0);
  }, [inbox]);

  const toggleRow = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === filteredInbox.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredInbox.map((item) => item.approval_request_id)));
    }
  };

  const handleApprove = async (e, id) => {
    e.stopPropagation();
    try {
      await approveMutation.mutateAsync({ id });
      toast.success("Request approved");
    } catch {
      // error handled by mutation onError
    }
  };

  const handleDecisionClick = (e, id, action) => {
    e.stopPropagation();
    setDecisionPopover((prev) =>
      prev?.id === id && prev?.action === action ? null : { id, action }
    );
  };

  const handleDecisionConfirm = async ({ comment }) => {
    if (!decisionPopover) return;
    const { id, action } = decisionPopover;
    const isReturn = action === "return";
    try {
      const fullComment = comment.trim() || (isReturn ? undefined : "Rejected");
      if (isReturn) {
        if (!fullComment) return; // return requires an actual comment, no default
        await returnMutation.mutateAsync({ id, comment: fullComment });
        toast.success("Request returned to preparer");
      } else {
        await rejectMutation.mutateAsync({ id, comment: fullComment });
        toast.success("Request rejected");
      }
      setDecisionPopover(null);
    } catch {
      // error handled by mutation onError
    }
  };

  if (isLoading) {
    return (
      <Fragment>
        <AdministrationHeader title="Approvals" showPeriodSelector={false} />
        <div className="px-8 py-6">
          <div className="flex flex-wrap gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-card-bd rounded-card px-4 py-3 w-[240px]">
                <Skeleton width={90} height={12} />
                <div className="mt-2"><Skeleton width={70} height={24} /></div>
                <div className="mt-2"><Skeleton width={120} height={12} /></div>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} width="100%" height={64} />
            ))}
          </div>
        </div>
      </Fragment>
    );
  }

  if (error) {
    const isUnauthorized = error?.status === 401 || error?.status === 403;
    return (
      <Fragment>
        <AdministrationHeader title="Approvals" showPeriodSelector={false} />
        <div className="px-8 py-6">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-danger mb-2">
              {isUnauthorized ? "Not authorized" : "Error loading approvals"}
            </div>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary text-white rounded-ctrl hover:bg-primary-hover cursor-pointer"
            >
              Retry
            </button>
          </div>
        </div>
      </Fragment>
    );
  }

  const selectAllChecked = selectedRows.size === filteredInbox.length && filteredInbox.length > 0;
  const isSearching = searchQuery.trim().length > 0;

  // Filter dropdowns live inside the table canvas (controls slot), matching /allocation.
  // TODO: Wire department/risk filters when the backend exposes those fields.
  const filterControls = (
    <>
      <FilterDropdown
        ariaLabel="Filter by department"
        value=""
        onChange={() => {}}
        minWidth={160}
        options={[{ value: "", label: "All Departments" }]}
      />
      <FilterDropdown
        ariaLabel="Filter by risk level"
        value=""
        onChange={() => {}}
        minWidth={150}
        options={[{ value: "", label: "Risk Level: All" }]}
      />
    </>
  );

  const columns = [
    {
      id: "select",
      align: "center",
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      Header: () => (
        <input
          type="checkbox"
          checked={selectAllChecked}
          onChange={toggleAll}
          aria-label="Select all"
          className="w-4 h-4 rounded border-border-strong cursor-pointer"
        />
      ),
      Cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedRows.has(row.original.approval_request_id)}
            onChange={() => toggleRow(row.original.approval_request_id)}
            aria-label="Select request"
            className="w-4 h-4 rounded border-border-strong cursor-pointer"
          />
        </div>
      ),
    },
    {
      id: "decision",
      Header: "Decision",
      width: 240,
      minWidth: 170,
      Cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text">
              {getDecisionLabel(item.impact_amount, item.transition_type)}
            </div>
            <div className="text-xs text-text-2 mt-0.5 truncate">
              {getDecisionContext(item)}
            </div>
          </div>
        );
      },
    },
    {
      id: "impact",
      Header: "Impact",
      width: 120,
      minWidth: 110,
      maxWidth: 150,
      Cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="min-w-0">
            <div className="text-sm font-medium text-text">
              {formatImpactAmount(item.impact_amount, "USD")}
            </div>
            <div className="text-xs text-text-2 mt-0.5">
              {formatImpactPercent(item.impact_amount, item.previous_amount)}
            </div>
          </div>
        );
      },
    },
    {
      id: "governance",
      Header: "Governance reason",
      width: 260,
      minWidth: 150,
      Cell: ({ row }) => (
        <div className="text-sm text-text-2 truncate min-w-0">
          {row.original.governance_trigger_summary || row.original.impact_summary || "—"}
        </div>
      ),
    },
    {
      id: "requester",
      Header: "Requester",
      width: 170,
      minWidth: 130,
      Cell: ({ row }) => {
        const item = row.original;
        const requesterName = item.requester_name || item.requested_by || "Unknown";
        return (
          <div className="min-w-0">
            <div className="text-sm font-medium text-text truncate">{requesterName}</div>
            <div className="text-xs text-text-2 mt-0.5 truncate">{item.requester_role || "—"}</div>
          </div>
        );
      },
    },
    {
      id: "requested",
      Header: "Requested",
      align: "right",
      width: 110,
      minWidth: 90,
      maxWidth: 140,
      Cell: ({ row }) => (
        <span className="text-sm text-text-2">
          {row.original.created_at ? formatRelativeTime(row.original.created_at) : "—"}
        </span>
      ),
    },
    {
      id: "actions",
      align: "right",
      width: 260,
      minWidth: 230,
      maxWidth: 300,
      Header: () => null,
      Cell: ({ row }) => {
        const item = row.original;
        // Hard SoD, mirrored from the backend's checkHardSoD: the preparer of a request
        // may not decide it themselves (approve, reject, or return all count as "deciding").
        const isPreparer =
          !!currentUser?.id &&
          !!item.requested_by &&
          String(currentUser.id) === String(item.requested_by);
        if (isPreparer) {
          return (
            <div className="text-right text-xs text-text-3 italic" onClick={(e) => e.stopPropagation()}>
              You prepared this — can&apos;t decide it
            </div>
          );
        }
        const isPopoverOpen = decisionPopover?.id === item.approval_request_id;
        const popoverAction = isPopoverOpen ? decisionPopover.action : null;
        return (
          <div className="flex justify-end gap-2 relative" onClick={(e) => e.stopPropagation()}>
            <button
              ref={isPopoverOpen ? decisionBtnRef : undefined}
              type="button"
              onClick={(e) => handleDecisionClick(e, item.approval_request_id, "return")}
              disabled={returnMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium border border-border rounded-ctrl text-text bg-card hover:bg-surface-2 cursor-pointer disabled:opacity-50"
            >
              Return
            </button>
            <button
              type="button"
              onClick={(e) => handleDecisionClick(e, item.approval_request_id, "reject")}
              disabled={rejectMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium border border-border rounded-ctrl text-text bg-card hover:bg-surface-2 cursor-pointer disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={(e) => handleApprove(e, item.approval_request_id)}
              disabled={approveMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium rounded-ctrl text-white bg-success hover:opacity-90 cursor-pointer disabled:opacity-50"
            >
              Approve
            </button>

            {isPopoverOpen && (
              <RejectionPopover
                anchorRef={decisionBtnRef}
                action={popoverAction}
                onClose={() => setDecisionPopover(null)}
                onConfirm={handleDecisionConfirm}
                isSubmitting={
                  popoverAction === "return" ? returnMutation.isPending : rejectMutation.isPending
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Fragment>
      <AdministrationHeader title="Approvals" showPeriodSelector={false} />
      <div className="px-8 py-6">
        {/* KPI cards — left-aligned, matching the /allocation strip (shared KpiCard). */}
        <div className="flex flex-wrap gap-4 mb-6">
          <KpiCard
            className="flex-1 min-w-[200px] max-w-[260px]"
            label="Pending requests"
            value={inbox?.length ?? 0}
            sub="Awaiting your decision"
          />
          <KpiCard
            className="flex-1 min-w-[200px] max-w-[260px]"
            label="Total value"
            value={formatMoney(totalValue * 100, "USD")}
            sub="Across pending requests"
          />
          <KpiCard
            className="flex-1 min-w-[200px] max-w-[260px]"
            label="Forecast variance"
            value="—"
            sub="Not yet available"
            // TODO: backend does not yet provide aggregate forecast variance
          />
        </div>

        {/* Requests table — search + filters live inside the canvas, like /allocation */}
        <TableCard
          title="Requests"
          subtitle={`${totalCount} pending request${totalCount === 1 ? "" : "s"}`}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search request ID, vendor…"
          controls={filterControls}
        >
          {totalCount === 0 ? (
            <EmptyState
              icon={icon({ name: "inbox", style: "light" })}
              title={isSearching ? "No matching requests" : "No pending approvals"}
              message={
                isSearching
                  ? "No requests match your search."
                  : "You're all caught up. Items requiring your approval will appear here."
              }
              action={
                isSearching
                  ? {
                      label: "Clear search",
                      onClick: () => setSearchQuery(""),
                      variant: "secondary",
                    }
                  : undefined
              }
            />
          ) : (
            <FlexTable
              columns={columns}
              data={filteredInbox}
              getRowId={(row) => row.approval_request_id}
              onRowClick={(id) => navigate(`/approvals/${id}`)}
              rowAriaLabel={(item) => `Review request ${getDecisionContext(item)}`}
            />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};

export default ApprovalsInboxPage;
